/**
 * Vorschlagskachel — zeigt von außen eingelieferte Kartenvorschläge als Bild,
 * mit Plus-Knopf zum Übernehmen ins Deck. Admin-Werkzeug; die Sichtbarkeits-
 * Entscheidung trifft der Aufrufer. Rendert als unterste Sektion der
 * Kommandobrücke (`BridgeSection`, Standard offen solange Vorschläge da sind)
 * bzw. der mobilen Statistik-Seite. Der ✦-Reiter des Katalogs (`CatalogAdminTab`)
 * nutzt dieselbe Karten-Darstellung (`SuggestionCardTile`) in einem eigenen Raster.
 *
 * Diese Sektion selbst nutzt kein `ui/CardTile` bzw. `CardGrid`: Beide sind auf den breiten
 * Kartenbereich ausgelegt, die Brücke ist 320px schmal — dort steht eine Karte pro Zeile in
 * voller Spaltenbreite. Der ✦-Reiter braucht die Tile-Hülle dagegen (`SuggestionCardTile`
 * mit `asTile`), siehe dort.
 */
import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import CardImage from '../../../ui/CardImage';
import CardOverlays from '../../../ui/CardOverlays';
import CardTile from '../../../ui/CardTile';
import BridgeSection from './bridge/BridgeSection';

/**
 * Eine Vorschlagskarte als Bild mit Übernehmen-/Verwerfen-Overlay — von `SuggestionTile`
 * und `CatalogAdminTab` geteilt. `asTile` entscheidet über die Hülle: die Brücke/mobile
 * Statistik-Seite bleiben beim bloßen `CardImage` (320px schmale Spalte, `ui/CardTile` ist
 * auf den breiten Kartenbereich ausgelegt), der ✦-Reiter des Katalogs braucht die
 * `CardTile`-Optik (Fläche, Rahmen, Radius, Hover), damit die Kacheln zu den Geschwister-
 * Reitern passen. `onOpen` öffnet das Karten-Detail per Klick aufs Bild (Kachel-Klick-Ziel
 * bzw. `CardImage.onOpen`); Plus und ✕ bleiben davon unberührt.
 */
export function SuggestionCardTile({ card, canAdd = true, onAdd, onDismissCard, onOpen, asTile = false }) {
  const öffnen = typeof onOpen === 'function' ? () => onOpen(card) : undefined;
  const öffnenLabel = `${card.name} — Details`;
  const overlay = (
    <CardOverlays>
      <CardOverlays.Actions>
        {canAdd && (
          <CardOverlays.Button
            icon={Plus}
            label={`${card.name} zum Deck hinzufügen`}
            onClick={() => onAdd(card.id)}
          />
        )}
        <CardOverlays.Button
          icon={X}
          label={`${card.name} verwerfen`}
          onClick={() => onDismissCard(card.id)}
        />
      </CardOverlays.Actions>
    </CardOverlays>
  );

  // Die Karten kommen aus POST /api/cards/batch als DTO mit englischem Standardbild.
  if (asTile) {
    return (
      <CardTile scryfallId={card.id} onClick={öffnen} clickLabel={öffnenLabel}>
        <CardTile.Image
          src={card.imageUrl}
          backSrc={card.imageUrlBack}
          alt={card.name}
          orientation={card.imageOrientation}
        >
          {overlay}
        </CardTile.Image>
      </CardTile>
    );
  }

  return (
    <CardImage
      src={card.imageUrl}
      backSrc={card.imageUrlBack}
      alt={card.name}
      onOpen={öffnen}
      openLabel={öffnenLabel}
    >
      {overlay}
    </CardImage>
  );
}

export default function SuggestionTile({ cards, onAdd, onDismissCard, onDismissAll, onOpen }) {
  const [open, setOpen] = useState(true);

  // Kein Leerzustand: ohne Vorschläge steht die Sektion nicht in der Brücke.
  if (!cards || cards.length === 0) return null;

  return (
    <BridgeSection
      label="Vorschläge"
      value={cards.length}
      open={open}
      onToggle={() => setOpen((o) => !o)}
      actions={(
        <button
          type="button"
          className="bridge-suggestions__dismiss"
          onClick={onDismissAll}
          title="Vorschläge verwerfen"
          aria-label="Vorschläge verwerfen"
        >
          <X size={14} aria-hidden />
        </button>
      )}
    >
      <div className="bridge-suggestions__grid">
        {cards.map((card) => (
          <SuggestionCardTile key={card.id} card={card} onAdd={onAdd} onDismissCard={onDismissCard} onOpen={onOpen} />
        ))}
      </div>
    </BridgeSection>
  );
}
